import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, X } from '@phosphor-icons/react';
import { cn } from '@skupreme/shared/ui';
import { LoaderBar } from '@components/LoaderBar';
import { AppListPlaceholder } from '@components/Placeholder';
import { useMarketPlaceHook } from '@hooks/marketplace';
import {
  IntegrationList,
  MarketplaceStatus,
  MiraklContactSalesPopup,
  RegionPopup,
  ShopifyDomainPopup,
  ShopifyNoticePopup,
  TemuConnectPopup,
  NetSuiteConnectPopup,
  WCConnectPopup,
  WalmartConnectPopup,
  WayfairConnectPopup,
} from '@page-components/marketplace-integration';
import { SearsConnectPopup } from '@page-components/marketplace-integration/SearsConnectPopup';
import { ConnectedMarkets } from '@page-components/onboarding/Connecting/ConnectedMarkets';
import { CommonUtility } from '@utility/common';
import {
  ErrorMessageConstant,
  marketplaceNames,
} from '@utility/constants/common';
import MarketPlaceService from '@utility/services/marketplace';
import OAuthService from '@utility/services/oauth';
import { ToastMessage } from '@utility/toast';
import { TargetPlusConnectPopup } from '@page-components/marketplace-integration/TargetPlusConnectPopup';
import SellerCloudConnectPopup from '@page-components/marketplace-integration/SellerCloudConnectPopup';

type StatusBoxItem = {
  key?: string;
  text?: string;
  desc?: string;
  status?: number | boolean;
};

type StatusBoxProps = {
  items: StatusBoxItem[];
  marketplace: string;
  isError?: boolean;
};

/** A marketplace's connection checks, or its errors, as a ticked list. */
export function StatusBox({
  items,
  marketplace,
  isError = false,
}: StatusBoxProps) {
  return (
    <div className="sk:flex sk:flex-col sk:gap-2 sk:text-fg">
      <div className="sk:text-sm sk:font-semibold">
        {marketplace} Connection{' '}
        {!isError ? 'Status' : `Error${items.length > 1 ? 's' : ''}`}
      </div>
      <ul className="sk:m-0 sk:flex sk:list-none sk:flex-col sk:gap-2 sk:p-0">
        {items.map(item => {
          const Icon = item.status ? Check : X;
          return (
            <li
              key={item.key}
              className={cn(
                'sk:flex sk:gap-2',
                isError ? 'sk:items-start' : 'sk:items-center',
              )}
            >
              <Icon
                weight="bold"
                size={18}
                aria-hidden
                className={cn(
                  'sk:shrink-0',
                  item.status ? 'sk:text-success' : 'sk:text-danger',
                )}
              />
              <div className="sk:flex sk:min-w-0 sk:flex-col sk:gap-0.5">
                {item?.text && (
                  <span className="sk:text-dense">{item.text}</span>
                )}
                {item?.desc && (
                  <span className="sk:text-xs sk:text-fg-secondary">
                    {item.desc}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function MarketplacesTab({ permission }) {
  const {
    connectedMarketPlaces,
    tobeAddMarketPlaces,
    loading,
    setConnectedMarketPlaces,
    marketplaces,
    refreshMetadata,
    continents,
  } = useMarketPlaceHook();

  useEffect(() => {
    refreshMetadata();
  }, []);

  const isUser = useMemo(() => permission === 'user', [permission]);

  const [showShopifyModal, setShowShopifyModal] = useState(false);
  const [shopifyNoticeModal, setShopifyNoticeModal] = useState('');
  const [showWalmartModal, setShowWalmartModal] = useState(false);
  const [showSearsModal, setShowSearsModal] = useState(false);
  const [showTargetPlusModal, setTargetPlusModal] = useState(false);
  const [showWCModal, setShowWCModal] = useState(false);
  const [showTemuModal, setShowTemuModal] = useState(false);
  const [showWayfairModal, setShowWayfairModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const disconnectInFlightRef = useRef(false);
  const [regions, setRegions] = useState([]);
  const [showNetSuiteModal, setShowNetSuiteModal] = useState(false);
  const [showSellerCloudModal, setShowSellerCloudModal] = useState(false);
  const [showMiraklModal, setShowMiraklModal] = useState(false);
  const [showRegionModal, setShowRegionModal] = useState(false);
  const [currentTab, setCurrentTab] = useState('');
  const [filteredMarketplaces, setFilteredMarketplaces] = useState([]);

  useEffect(() => {
    setCurrentTab(continents.length > 0 ? continents[0] : '');
  }, [continents]);

  useEffect(() => {
    if (currentTab === '') {
      setFilteredMarketplaces([]);
      return;
    }

    const markets = connectedMarketPlaces.filter(
      market => market.continent.name === currentTab,
    );
    // @ts-expect-error - TS2345: Argument of type 'IConnectedMarketplace[]' is not assignable to parameter of typ...
    setFilteredMarketplaces(markets);
  }, [currentTab, connectedMarketPlaces]);

  const connectOAuth = (item, store) => {
    let url = item.oauth_login_uri;
    if (item.status === MarketplaceStatus.Shopify) {
      url = item.oauth_login_uri.replace('__SHOPIFY_STORE__', store);
    }

    window.location.href = url;
  };

  const connectOauthViaUri = item => {
    const url = item.oauth_login_uri;
    window.open(url, '_blank');
  };

  const shopifyDomainModalClose = store => {
    setShopifyNoticeModal('');
    if (store) {
      connectOAuth(selectedItem, store);
    }
  };

  const shopifyOAuth = () => {
    const shopifyStoreSkupreme =
      'https://apps.shopify.com/ecom-prism?show_store_picker=1';
    window.location.href = shopifyStoreSkupreme;
  };

  // add state once

  const marketplaceClick = (item, store = '') => {
    const t = String(item?.type || '').toLowerCase();

    // SellerCloud → open modal
    if (t.includes('sellercloud')) {
      setSelectedItem(item);
      setShowSellerCloudModal(true);
      return;
    }

    switch (item.status) {
      case MarketplaceStatus.OAuthFlow:
      case MarketplaceStatus.Bonanza:
        if (['target_us'].includes(item.name)) {
          setSelectedItem(item);
          setTargetPlusModal(true);
          break;
        } else {
          connectOAuth(item, store);
        }
        break;

      case MarketplaceStatus.Shopify:
        if (CommonUtility.isProductionEnv()) {
          setShopifyNoticeModal('Prod');
        } else {
          setSelectedItem(item);
          setShowShopifyModal(true);
        }
        break;

      case MarketplaceStatus.Walmart:
        setSelectedItem(item);
        setShowWalmartModal(true);
        break;

      case MarketplaceStatus.Sears:
        setSelectedItem(item);
        setShowSearsModal(true);
        break;

      case MarketplaceStatus.Facebook:
      case MarketplaceStatus.BigCommerce:
        connectOauthViaUri(item);
        break;

      case MarketplaceStatus.WooCommerce:
        setShowWCModal(true);
        break;

      case MarketplaceStatus.Wayfair:
        setShowWayfairModal(true);
        break;

      case MarketplaceStatus.Temu:
        setSelectedItem(item);
        setShowTemuModal(true);
        break;

      case MarketplaceStatus.NetSuite:
        setSelectedItem(item);
        setShowNetSuiteModal(true);
        break;

      default:
        break;
    }
  };

  const walmartClose = success => {
    setShowWalmartModal(false);
    if (success) {
      refreshMetadata();
    }
  };

  const searsClose = success => {
    setShowSearsModal(false);
    if (success) {
      refreshMetadata();
    }
  };

  const targetPlusClose = success => {
    setTargetPlusModal(false);
    if (success) {
      refreshMetadata();
    }
  };

  const disconnectMarket = async item => {
    if (disconnectInFlightRef.current) return;

    try {
      if (!item.credential_id || !item._id) {
        ToastMessage.error(
          'This marketplace connection is missing its exact credential or marketplace ID and cannot be safely disconnected. Please contact support.',
        );
        return;
      }

      disconnectInFlightRef.current = true;
      setSaving(true);
      await OAuthService.disconnectMarketplace(item._id, item.credential_id);

      ToastMessage.success(
        `${item.alias} marketplace disconnected successfully.`,
      );
      refreshMetadata();
    } catch (error) {
      ToastMessage.error(error?.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      disconnectInFlightRef.current = false;
      setSaving(false);
    }
  };

  const updateCredentials = async (item, key, value, sync_settings) => {
    try {
      const temp = {
        item_sync: item.sync_settings?.item_sync || false,
        order_sync: item.sync_settings?.order_sync || false,
        fba_shipment_backfill_sync:
          item.sync_settings?.fba_shipment_backfill_sync || false,
        order_backfill_sync: item.sync_settings?.order_backfill_sync || false,
        set_first_sync_state: item.sync_settings?.set_first_sync_state || false,
        inventory_sync: item.sync_settings?.inventory_sync || false,
        fbm_inventory_sync: item.sync_settings?.fbm_inventory_sync || false,
        fbm_catalog_sync: item.sync_settings?.fbm_catalog_sync || false,
        [key]: value,
        marketplace_id: item._id,
        credential_id: item.credential_id,
      };
      await MarketPlaceService.credentials(temp);
      let tempFilteredMarketPlaces = [];
      for (const market of filteredMarketplaces) {
        if (
          // @ts-expect-error - TS2339: Property 'marketplace_id' does not exist on type 'never'.
          market.marketplace_id[0] === item.marketplace_id[0] &&
          // @ts-expect-error - TS2339: Property 'credential_id' does not exist on type 'never'.
          item.credential_id === market.credential_id
        ) {
          // @ts-expect-error - TS2339: Property 'sync_settings' does not exist on type 'never'.
          market.sync_settings = sync_settings;
          tempFilteredMarketPlaces = [...tempFilteredMarketPlaces, market];
        } else {
          tempFilteredMarketPlaces = [...tempFilteredMarketPlaces, market];
        }
      }
      const tempData = connectedMarketPlaces.map(market => {
        const t = {
          ...market,
        };
        // @ts-expect-error - TS2551: Property 'marketplaces_details' does not exist on type 'IConnectedMarketplace'. ...
        market?.marketplaces_details.forEach((mDetail, index) => {
          if (mDetail._id === item._id) {
            // @ts-expect-error - TS2551: Property 'marketplaces_details' does not exist on type '{ _id: string; status: n...
            t.marketplaces_details[index].sync_settings = {
              // @ts-expect-error - TS2551: Property 'marketplaces_details' does not exist on type '{ _id: string; status: n...
              ...(t.marketplaces_details[index].sync_settings || {}),
              [key]: value,
            };
          }
        });
        if (key === 'multi_location') {
          t[key] = value;
        }
        return t;
      });
      setFilteredMarketplaces(tempFilteredMarketPlaces);
      setConnectedMarketPlaces(tempData);
    } catch (error) {
      ToastMessage.error(error?.error || ErrorMessageConstant.defaultAPIError);
    }
  };

  const integrationClick = item => {
    const miraklSource = `${item?.type || ''} ${item?.name || ''} ${
      item?.alias || ''
    }`
      .toLowerCase()
      .trim();
    if (miraklSource.includes('mirakl')) {
      setShowMiraklModal(true);
      return;
    }

    const regionOptions = [];

    const filteredToBeAdded = tobeAddMarketPlaces.filter(
      x => x.type === item.type,
    );

    if (CommonUtility.isValidArray(filteredToBeAdded)) {
      // @ts-expect-error - TS2345: Argument of type 'IMarketplaceMeta' is not assignable to parameter of type 'neve...
      regionOptions.push(...filteredToBeAdded);
    }

    if (
      [
        marketplaceNames.shopify,
        marketplaceNames.eBay,
        marketplaceNames.tiktok,
      ].includes(item.type)
    ) {
      const filteredConnected = connectedMarketPlaces.filter(
        x => x.type === item.type,
      );
      if (CommonUtility.isValidArray(filteredConnected)) {
        // @ts-expect-error - TS2345: Argument of type 'IConnectedMarketplace' is not assignable to parameter of type ...
        regionOptions.push(...filteredConnected);
      }
    }

    if (regionOptions.length > 1) {
      const temp = regionOptions.map(regionItem => ({
        // @ts-expect-error - TS2339: Property 'marketplace_region_id' does not exist on type 'never'.
        value: regionItem.marketplace_region_id,
        // @ts-expect-error - TS2339: Property 'alias' does not exist on type 'never'.
        text: regionItem.alias,
        // @ts-expect-error - TS2339: Property 'countries_code' does not exist on type 'never'.
        flag: regionItem.countries_code,
        item: regionItem,
      }));
      // @ts-expect-error - TS2345: Argument of type '{ value: any; text: any; flag: any; item: never; }[]' is not a...
      setRegions(temp);
      setShowRegionModal(true);
    } else {
      marketplaceClick(regionOptions[0]);
    }
  };

  const regionClose = item => {
    setShowRegionModal(false);
    setRegions([]);
    if (item) {
      marketplaceClick(item);
    }
  };

  return (
    <div>
      {saving && <LoaderBar content="Updating Marketplace" />}
      {loading ? (
        <AppListPlaceholder hideImages size={3} />
      ) : (
        <div className="sk:flex sk:justify-center">
          <ConnectedMarkets
            connectedMarketPlaces={connectedMarketPlaces}
            continents={continents}
            isUser={isUser}
            updateCredentials={updateCredentials}
            doProgressSync={false}
            disconnectMarket={disconnectMarket}
            reconnect={marketplaceClick}
            refreshMetadata={refreshMetadata}
          />
        </div>
      )}
      {marketplaces.length > 0 && (
        <div className="sk:my-10">
          <IntegrationList
            data={marketplaces}
            marketplaceClick={integrationClick}
            permission={permission}
          />
        </div>
      )}

      <ShopifyDomainPopup
        open={showShopifyModal}
        closeModal={store => {
          setShowShopifyModal(false);
          setShopifyNoticeModal(store);
        }}
      />
      <ShopifyNoticePopup
        open={shopifyNoticeModal !== ''}
        closeModal={isSelect => {
          setShopifyNoticeModal('');
          if (isSelect)
            CommonUtility.isProductionEnv()
              ? shopifyOAuth()
              : shopifyDomainModalClose(shopifyNoticeModal);
        }}
      />
      <WalmartConnectPopup
        open={showWalmartModal}
        closeModal={success => walmartClose(success)}
        item={selectedItem}
      />
      <SearsConnectPopup
        open={showSearsModal}
        closeModal={success => searsClose(success)}
        item={selectedItem}
      />
      <TargetPlusConnectPopup
        open={showTargetPlusModal}
        closeModal={success => targetPlusClose(success)}
        item={selectedItem}
      />
      <WCConnectPopup
        open={showWCModal}
        closeModal={() => setShowWCModal(false)}
      />
      <TemuConnectPopup
        open={showTemuModal}
        closeModal={() => setShowTemuModal(false)}
        reload={refreshMetadata}
        // @ts-expect-error - TS2339: Property 'logo_uri' does not exist on type 'never'.
        image={selectedItem?.logo_uri}
        marketplaceId={
          // @ts-expect-error - TS2339: Property 'marketplace_id' does not exist on type 'never'.
          Array.isArray(selectedItem?.marketplace_id)
            ? // @ts-expect-error - TS2339: Property 'marketplace_id' does not exist on type 'never'.
              selectedItem.marketplace_id.find(Boolean)
            : // @ts-expect-error - TS2339: Property 'marketplace_id' does not exist on type 'never'.
              selectedItem?.marketplace_id
        }
      />
      <NetSuiteConnectPopup
        open={showNetSuiteModal}
        closeModal={() => setShowNetSuiteModal(false)}
        reload={refreshMetadata}
        marketplaceId={
          // @ts-expect-error - TS2339: Property 'marketplace_id' does not exist on type 'never'.
          Array.isArray(selectedItem?.marketplace_id)
            ? // @ts-expect-error - TS2339: Property 'marketplace_id' does not exist on type 'never'.
              selectedItem.marketplace_id.find(Boolean)
            : // @ts-expect-error - TS2339: Property 'marketplace_id' does not exist on type 'never'.
              selectedItem?.marketplace_id
        }
      />
      <WayfairConnectPopup
        open={showWayfairModal}
        closeModal={() => setShowWayfairModal(false)}
      />
      <RegionPopup
        closeModal={item => regionClose(item)}
        open={showRegionModal}
        regions={regions}
      />
      <MiraklContactSalesPopup
        open={showMiraklModal}
        closeModal={() => setShowMiraklModal(false)}
      />
      {/* @ts-expect-error - TS2741: Property 'onSave' is missing in type '{ refetch: () => void ... */}
      <SellerCloudConnectPopup
        refetch={refreshMetadata}
        open={showSellerCloudModal}
        onClose={() => setShowSellerCloudModal(false)}
        marketplace_id={
          // @ts-expect-error - TS2339: Property 'marketplace_id' does not exist on type 'never'.
          Array.isArray(selectedItem?.marketplace_id)
            ? // @ts-expect-error - TS2339: Property 'marketplace_id' does not exist on type 'never'.
              selectedItem?.marketplace_id[0]
            : // @ts-expect-error - TS2339: Property 'marketplace_id' does not exist on type 'never'.
              selectedItem?.marketplace_id
        }
        // @ts-expect-error - TS2339: Property 'marketplace_region_id' does not exist on type 'never'.
        marketplace_region_id={selectedItem?.marketplace_region_id}
        // @ts-expect-error - TS2339: Property 'alias' does not exist on type 'never'.
        marketplace_name={selectedItem?.alias || selectedItem?.name}
      />
      <div className="sk:mx-4 sk:flex sk:justify-center">
        <p className="sk:text-xs sk:text-fg-tertiary">
          The term &apos;Etsy&apos; is a trademark of Etsy, Inc. This
          application uses the Etsy API but is not endorsed or certified by
          Etsy, Inc.
        </p>
      </div>
    </div>
  );
}
